import { ChevronUpDownIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

const SelectInput = ({
  options,
  selected,
  onSelect,
  onChange,
  placeholder,
  disabled,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!options) {
      setSelectedOption(null);
      return;
    }

    if (selected === undefined) {
      setSelectedOption(null);
    } else if (
      Array.isArray(options) &&
      options.length > 0 &&
      typeof options[0] === "object"
    ) {
      setSelectedOption(
        options.find((option) => option.value === selected) || null,
      );
    } else {
      setSelectedOption(options.find((option) => option === selected) || null);
    }
  }, [selected, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelection = (option) => {
    const value = option.value !== undefined ? option.value : option;
    const label = option.label !== undefined ? option.label : option;

    if (onChange) {
      onChange(value, label);
      setShowOptions(false);
    }

    if (onSelect) {
      onSelect(value, label);
      setShowOptions(false);
    }
  };

  const handleInputClick = () => {
    setShowOptions(!showOptions);
  };

  const positionDropdown = () => {
    if (containerRef.current && dropdownRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;

      const shouldOpenUp =
        spaceBelow < dropdownRect.height && spaceAbove >= dropdownRect.height;

      if (shouldOpenUp) {
        dropdownRef.current.style.top = `-${dropdownRect.height + 8}px`;
      } else {
        dropdownRef.current.style.top = `${containerRect.height}px`;
      }

      if (selectedOption) {
        const selectedIndex = options?.findIndex((option) =>
          option.value
            ? option.value === selectedOption.value
            : option === selectedOption,
        );
        const selectedElement = dropdownRef.current.children[selectedIndex];

        if (selectedElement) {
          if (!shouldOpenUp) {
            dropdownRef.current.scrollTop =
              selectedElement.offsetTop - dropdownRef.current.offsetTop + 35;
          } else {
            dropdownRef.current.scrollTop =
              selectedElement.offsetTop + dropdownRef.current.offsetTop + 50;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (showOptions) {
      positionDropdown();
    }
  }, [showOptions, selectedOption]);

  const getOptionLabel = (option) => (option.label ? option.label : option);
  const getOptionValue = (option) => (option.value ? option.value : option);

  return (
    <div ref={containerRef} className="relative cursor-default">
      <button
        disabled={disabled}
        type="button"
        className={clsx(
          disabled && "opacity-50 cursor-wait",
          "cursor-pointer w-full text-left bg-white border border-gray-300 rounded-lg shadow-sm py-2 px-4 focus:outline-none focus:ring-offset-purple focus:ring-1 ring-purple",
        )}
        onClick={handleInputClick}
      >
        {selectedOption ? getOptionLabel(selectedOption) : placeholder}
      </button>
      <ChevronUpDownIcon className="absolute top-[11px] right-[8px] w-4 h-4 text-zinc-400" />

      {showOptions && (
        <ul
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[27em] overflow-y-auto scroll-color"
        >
          {options &&
            options.map((option, index) => (
              <li
                key={option.value ? option.value : index}
                onClick={() => handleSelection(option)}
                className={`px-4 py-1 cursor-pointer hover:bg-gray-100 ${
                  selectedOption &&
                  getOptionValue(selectedOption) === getOptionValue(option)
                    ? "bg-yellow"
                    : ""
                }`}
              >
                {getOptionLabel(option)}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default SelectInput;
